import { Box, Button, Container, Divider, Grid, ListItem, Paper, TablePagination, TextField, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import startOfDay from "date-fns/startOfDay";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { AutoSizer } from "react-virtualized";
import { FixedSizeList } from "react-window";
import CustomDatePicker from '../../components/date-picker/CustomDatePicker';
import DropdownSelect from "../../components/dropdown-select/DropdownSelect";
import { HeaderBlock } from "../../components/header-block/HeaderBlock";
import { Loading } from "../../components/loading/Loading";
import OrderManagerCard from "../../components/order-manager-card/OrderManagerCard";
import OrderManagerDetailDialog from "../../components/order-manager-detail-dialog/OrderManagerDetailDialog";
import PageContainer from "../../components/page-container/PageContainer";
import { PagePlaceholder } from '../../components/page-placeholder/PagePlaceholder';
import { clearOrders, getOrder, getOrdersByFilter, getRelatedOrders } from "../../state/actions/OrderActions";
import { getRoutes } from '../../state/actions/RouteActions';
import { OrderQueueOriginType } from '../../state/constants/OrderQueueOriginType';
import { SalesOrgIds } from '../../state/constants/SalesOrgIds';
import { SourceType } from '../../state/constants/SourceType';
import { StatusType } from '../../state/constants/StatusType';



const useStyles = makeStyles({
    wrapper: {
        padding: "30px",
    },
    contentGrid: {
        width: "100%",
        height: props =>
            props.error ? "calc(100% - 205px)" : "calc(100% - 185px)",
    },
    listView: {
        position: "relative",
        height: "100%",
    },
    listItem: {
        padding: "8px 16px 8px 16px",
        "&:first-child": {
            paddingTop: "16px"
        },
        "&:last-child": {
            paddingBottom: "16px"
        }
    },
    filterPaper: {
        borderRadius: 0,
        position: "relative",
        zIndex: 1,
        padding: "8px 16px 8px 16px",
    },
    submitButtonContainer: {
        justifyContent: "flex-end",
        display: "flex",
        alignItems: "",
    },
    searchButton: {
        margin: "8px 0 8px 0",
        height: "40px"
    },
    divider: {
        // margin: "16px 0 16px 0"
    },
    pagination: {
        width: "100%",
        bottom: "0",
        left: "0",
        right: "0",
        position: "fixed",
        backgroundColor: "#FFF",
        border: "1px solid rgba(0, 0, 0, 0.12)"
    },
    topGrid: {
        paddingBottom: "0 !important",
    },
    bottomGrid: {
        paddingTop: "0 !important",
    }
})

export default function OrderManagerPage() {
    const orders = useSelector(state => state.OrderReducer.orders);
    const routes = useSelector(state => state.RouteReducer.routes);
    const relatedOrders = useSelector(state => state.OrderReducer.relatedOrders)
    const orderCount = useSelector(state => state.OrderReducer.orderCount, shallowEqual);

    const didMount = useRef(false);
    const [t, i18n] = useTranslation()

    const [loading, setLoading] = useState(false)
    const [modalOrder, setModalOrder] = useState({})
    const [modalOpen, setModalOpen] = useState(false)
    const [searchByIdError, setSearchByIdError] = useState(false)

    const [statusFilter, setStatusFilter] = useState([StatusType.Confirmed.key])
    const [sourceFilter, setSourceFilter] = useState([])
    const [firstDateSelected, setFirstDateSelected] = useState(startOfDay(new Date()))
    const [secondDateSelected, setSecondDateSelected] = useState(null)
    const [idFilter, setIdFilter] = useState([])
    const [routeFilter, setRouteFilter] = useState([])
    const [salesOrgFilter, setSalesOrgFilter] = useState([])

    const [searchedData, setSearchedData] = useState(Object.values(orders))
    const [originalData, setOriginalData] = useState(Object.values(orders))

    const [currentPage, setCurrentPage] = useState(0)
    const [pageSize, setPageSize] = useState(50)
    const [newData, setNewData] = useState(false)

    const [routeData, setRouteData] = useState([])
    const [relatedOrdersLoading, setRelatedOrdersLoading] = useState(false)
    const classes = useStyles({ error: searchByIdError })

    const flavour = process.env.REACT_APP_FLAVOUR

    useEffect(() => {
        return () => {
            dispatch(clearOrders(() => { }))
            didMount.current = false;
        };
    }, [])

    useEffect(() => {
        if (!didMount.current) {
            dispatch(getRoutes(() => {

            }));
        }
    }, [])

    useEffect(() => {
        setRouteData(Object.keys(routes).sort().map(item => ({ key: item, value: item })))
    }, [routes])

    useEffect(() => {
        if (didMount.current) {
            setCurrentPage(0)
        }

    }, [pageSize])

    useEffect(() => {
        if (didMount.current) {
            performApiSearch()
        }

    }, [pageSize, currentPage])

    useEffect(() => {
        if (didMount.current || newData === true) {
            setSearchedData(Object.values(orders))
            setOriginalData(Object.values(orders))
            setNewData(false)
        } else {
            didMount.current = true;
        }

    }, [newData])

    useEffect(() => {
        const orderId = modalOrder == null ? null : modalOrder.data == null ? null : modalOrder.data.OrderId != null ? modalOrder.data.OrderId : modalOrder.data.MiddlewareId
        if (orderId != null) {
            setRelatedOrdersLoading(true)
            dispatch(getRelatedOrders(orderId, () => {
                setRelatedOrdersLoading(false)
            }))
        }

    }, [modalOrder])

    const dispatch = useDispatch();

    function renderOrderStatusSummary() {
        const cards = [...Array(3).keys()].map(item => {
            return {
                key: item,
                title: "Data" + item,
                count: item
            }
        })
        return (
            <Grid container spacing={3}>
                {cards.map(card => {
                    return (
                        <Grid item xs={4} key={card.key}>
                            <Typography variant="body2">{`${card.title}: ${card.count}`}</Typography>
                        </Grid>
                    );
                })}
            </Grid>
        );
    }

    function createDetails(item) {
        return {
            details: [
                {
                    title: t('order-manager.order-type'),
                    value: item.OrderType
                },
                {
                    title: t('order-manager.order-date'),
                    value: new Date(item.UpdateDate || "").toLocaleString()
                },
                {
                    title: t('order-manager.middleware-id'),
                    value: item.MiddlewareId
                },
                {
                    title: t('order-manager.order-no'),
                    value: item.OrderId
                },
                {
                    title: t('order-manager.order-quantity'),
                    value: t("order-manager.order-quantity-merge", { cases: t("order-manager.order-quantity-case", { count: item.TotalCases }), eaches: t("order-manager.order-quantity-each", { count: item.TotalEaches }) })
                },
                {
                    title: t('order-manager.total'),
                    value: item.TotalPrice
                }
            ]
        }
    }

    function renderCardItem(item) {
        const data = item.data
        const type = item.type
        switch (type) {
            case OrderQueueOriginType.Order: {
                return (
                    <OrderManagerCard
                        hover
                        id={data.id}
                        title={data.MiddlewareId || data.OrderId}
                        subtitle={data.CustomerId + " | " + data.SourceName}
                        status={data.Status}
                        data={createDetails(data).details}
                        onClick={(event) => {
                            setModalOrder(item)
                            setModalOpen(true)
                        }}
                    />
                )
            }
            case OrderQueueOriginType.OrderIdPoison: case OrderQueueOriginType.OrderSubEgressPoison: case OrderQueueOriginType.OrderSubIngressPoison: {
                return (
                    <OrderManagerCard
                        hover
                        id={data.id}
                        title={data.OrderId}
                        subtitle={type}
                        status={t('order-manager.poison-queued')}
                        data={[]}
                        displayMessage={t('order-manager.incorrect-data', {type: type})}
                        onClick={(event) => {
                            setModalOrder(item)
                            setModalOpen(true)
                        }}
                    />
                )
            }
            default: {
                return (
                    <OrderManagerCard
                        hover
                        id={data.id}
                        title={data.OrderId}
                        subtitle={type}
                        status={t('order-manager.queued')}
                        data={[]}
                        displayMessage={t('order-manager.currently-queued', {type: type})}
                        onClick={(event) => {
                            setModalOrder(item)
                            setModalOpen(true)
                        }}
                    />
                )
            }
        }


    }

    function renderItems() {
        const data = searchedData

        const Row = ({ index, style }) => {
            return (
                <ListItem style={style} key={index} className={classes.listItem}>
                    {renderCardItem(data[index])}
                </ListItem>
            )
        }

        return (
            <Box className={classes.contentGrid}>
                {data.length === 0 ?
                    <PagePlaceholder text={t('order-manager.none-to-display')} /> :
                    <AutoSizer>
                        {({ height, width }) => (
                            <FixedSizeList
                                className
                                height={height}
                                itemCount={data.length}
                                itemSize={190}
                                width={width}
                            >
                                {Row}
                            </FixedSizeList>
                        )}
                    </AutoSizer>
                }
            </Box>
        );
    }

    function performApiSearch() {
        let filter = {
            Entries: 50,
            Page: 1,
            Status: [],
            Source: [],
            EndDate: null,
            StartDate: null,
            Route: [],
            Id: []
        }
        filter.Entries = pageSize
        filter.Page = currentPage + 1
        filter.Status = statusFilter
        filter.Source = sourceFilter
        filter.StartDate = firstDateSelected
        filter.EndDate = secondDateSelected
        filter.Route = routeFilter
        filter.SalesOrgIds = salesOrgFilter
        // filter.Id = idFilter

        setLoading(true)
        setSearchByIdError(false)
        dispatch(getOrdersByFilter(filter, () => {
            console.log("Orders retrieved")
            setNewData(true)
            setLoading(false)
        }))
    }

    function performIdFetch() {
        if (idFilter.length > 0) {
            setLoading(true)
            setSearchByIdError(false)
            setCurrentPage(0)
            dispatch(getOrder(idFilter, () => {
                console.log("Order retrieved")
                setNewData(true)
                setLoading(false)
            }))
        } else {
            setSearchByIdError(true)
        }
    }

    return (
        <Container maxWidth="false">
            <HeaderBlock title={t('order-manager.order-manager')} />

            <PageContainer hasFooter>
                <Paper className={classes.filterPaper}>
                    <Grid container spacing={2}>
                        <Grid item xs={4} className={classes.topGrid}>
                            <DropdownSelect
                                fullWidth
                                multiple
                                label={t('order-manager.status-filter')}
                                handleChange={(values) => {
                                    setStatusFilter(values)
                                }}
                                data={Object.values(StatusType)}
                                value={statusFilter}
                            />
                        </Grid>
                        <Grid item xs={4} className={classes.topGrid}>
                            <DropdownSelect
                                fullWidth
                                multiple
                                label={t('order-manager.source-filter')}
                                handleChange={(value) => {
                                    setSourceFilter(value)
                                }}
                                data={Object.values(SourceType)}
                                value={sourceFilter}
                            />
                        </Grid>
                        <Grid item xs={4} className={classes.topGrid}>
                            <CustomDatePicker
                                label={t('order-manager.date-filter')}
                                firstDateSelected={firstDateSelected}
                                secondDateSelected={secondDateSelected}
                                updateFirstDate={(date) => setFirstDateSelected(date)}
                                updateSecondDate={(date) => setSecondDateSelected(date)}
                            />
                        </Grid>
                        <Grid item xs={4} className={classes.bottomGrid}>
                            <DropdownSelect
                                fullWidth
                                multiple
                                label={t('order-manager.route-filter')}
                                handleChange={(value) => {
                                    setRouteFilter(value)
                                }}
                                data={routeData}
                                value={routeFilter}
                            />
                        </Grid>
                        <Grid item xs={4} className={classes.bottomGrid}>
                            <DropdownSelect
                                fullWidth
                                multiple
                                label={t('order-manager.sales-org-filter')}
                                handleChange={(value) => {
                                    setSalesOrgFilter(value)
                                }}
                                data={Object.values(SalesOrgIds()[flavour] || {})}
                                value={salesOrgFilter}
                            />
                        </Grid>
                        <Grid item xs={4} className={classes.bottomGrid}>
                            <Box className={classes.submitButtonContainer}>

                                <Button fullWidth className={classes.searchButton} variant="outlined" onClick={() => performApiSearch()}>
                                    {t('order-manager.filter-search')}

                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                    <Divider className={classes.divider} />
                    <Grid container spacing={2}>
                        <Grid item xs={9}>
                            <TextField
                                margin="dense"
                                variant="outlined"
                                fullWidth
                                onChange={event => {
                                    setIdFilter(event.target.value)
                                }}
                                label={t('order-manager.search')}
                                error={searchByIdError}
                                helperText={searchByIdError ? t('order-manager.id-required') : null}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <Box className={classes.submitButtonContainer}>
                                <Button className={classes.searchButton} fullWidth variant="outlined" onClick={() => performIdFetch()}>
                                    {t('order-manager.search-by-id')}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                    {/* <Divider className={classes.divider} />

                    <Search
                        fullWidth
                        searchField={"data.CustomerId"}
                        returnSearchMatches={data => {
                            setSearchedData(data)
                        }}
                        data={originalData}
                        searchTitle="Search Returned Entries"
                    /> */}
                </Paper>
                {
                    loading ?
                        <Loading /> :
                        renderItems()
                }
            </PageContainer>

            {/* <FooterBlock> */}
            <TablePagination
                className={classes.pagination}
                component="nav"
                page={currentPage}
                rowsPerPage={pageSize}
                count={orderCount}
                onChangePage={(evt, page) => {
                    setCurrentPage(page)
                }}
                onChangeRowsPerPage={(event) => {
                    setPageSize(event.target.value)
                    //get new data with rows per page
                }}
            />
            {/* </FooterBlock> */}
            <OrderManagerDetailDialog
                orderDetails={modalOrder}
                relatedOrders={
                    relatedOrders != null ?
                        Object.values(relatedOrders).filter(item => {
                            if (modalOrder.data == null || item.data == null) {
                                return true
                            }
                            return modalOrder.data.id != item.data.id
                        }) :
                        []
                }
                relatedOrdersLoading={relatedOrdersLoading}
                open={modalOpen}
                onClose={() => {
                    setModalOrder({})
                    setModalOpen(false)
                }}
            />

        </Container>
    );

}